import React from 'react';


import { Path } from '~/paths';
import { ChapterWrapper } from '~/ChapterWrapper';
import Footer from '~/Footer';
import { ELink } from '~/ExternalLink';

const diagram = require('../assets/images/trideni_kvant.png');
const gHisto = require('../assets/images/g_histo.png');
const gSloup = require('../assets/images/g_sloup.png');

const hist3graphs = require('../assets/images/hist_three_graphs.png');
const hist3graphsB = require('../assets/images/hist_three_graphs_B.png');

const h06to18 = require('../assets/images/h06to18.png');
const h05to19 = require('../assets/images/h05to19.png');
const h0p1 = require('../assets/images/h0p1.png');
const h0p05 = require('../assets/images/h0p05.png');
const h0p01 = require('../assets/images/h0p01.png');
const h0p6 = require('../assets/images/h0p6.png');

export const HistogramMeta = {
    title: "Třídění spojitého znaku, histogram",
    shortTitle: "Třídění, histogram",
    path: Path.histogram,
    element: (sectionNumber:string) => <Histogram sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

// https://www.umat.fekt.vut.cz/~fusekmi/ipt/04.pdf

const TOCSpec = [
    "hist-sturges",
    "hist-construction",
    "hist-comp-discrete-continuous",
    "hist-comparison-graphs",
    "hist-example",
];


export function Histogram({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={HistogramMeta.title} TOCSpec={TOCSpec}>

            <p>
            V této kapitole se zabýváme tříděním <i>spojitého kvantitativního</i> znaku
            (a je jedno, zda je intervalový či poměrový). Představte si měření
            fyzikálních veličin typu délka, hmotnost, teplota, koncentrace, absorbance, atp.
            </p>

            <p>
            Spojitý znak může nabývat nekonečného množství hodnot (reálná čísla,
            teoreticky s libovolnou přesností). Pokud měříme na větší množství platných číslic
            pak získaný datový soubor nejspíš nebude obsahovat opakované hodnoty
            (i když samozřejmě vyloučit se to nedá).
            </p>

            <p>
            <strong>Třídění</strong> znamená to, že si rozsah pozorovaných hodnot pokryjeme
            systémem navazujících intervalů (tříd), a u každé třídy spočítáme,
            kolik hodnot do ní spadá.
            </p>

            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={diagram} style={{width: "100%"}}/>
            </div>

            <h2 id="hist-sturges">Volba počtu tříd a jejich hranic</h2>

            <p>Počet tříd volíme tak, aby nebyl ani příliš velký, ani příliš malý.
            Máme-li např. 100 naměřených hodnot, je zbytečné dělit je do 100 tříd. Mnohé třídy by obsahovaly
            jen jednu hodnotu, nebo by byly prázdné.
            Naopak dělení např. do dvou tříd je příliš hrubé - ztratí se podkladový tvar rozdělení dat.
            Třídy volíme také s ohledem na to, aby měly pěkné středy a hranice - obzvláště pokud výsledná roztříděná
            data jsou prezentována lidem, kteří je mají číst a udělat si o nich představu. Třídy s hranicemi 1, 2, 3, 4, 5
            jsou např. v tomto ohledu lepší než třídy s hranicemi 1.12, 2.12, 3.12, 4.12, 5.12.
            </p>

            <p>
            Pokud bychom chtěli nějaký vztah, který nám dá hrubou představu o tom, jaký počet tříd zvolit,
            pak se tradičně používá např. Sturgesovo pravidlo. Toto pravidlo, i další, uvádíme v tabulce níže:
            </p>

            <table className="data-table">
                <thead>
                    <tr>
                        <th>Pravidlo</th>
                        <th>Vzorec</th>
                        <th>Pozn.</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Sturgesovo</td>
                        <td>{"$\\lceil 1 + \\log_2(n)\\rceil \\approx \\lceil 1 + 3.3 \\log(n) \\rceil$"}</td>
                        <td>Pro soubor dat symetrického charakteru.</td>
                    </tr>
                    <tr>
                        <td>odmocninové</td>
                        <td>{"$\\langle \\sqrt{n}, 2\\sqrt{n} \\rangle$"}</td>
                        <td>Pro soubor dat asymetrického charakteru.</td>
                    </tr>
                    <tr>
                        <td>Scott</td>
                        <td>{"$\\frac{3.49 s}{\\sqrt[3]{n}}$"}</td>
                        <td>{"$s$"} je odhad směrodatné odchylky.</td>
                    </tr>
                    <tr>
                        <td>Freedman-Diaconis</td>
                        <td>{"$2 \\frac{\\mathrm{IQR}}{\\sqrt[3]{n}}$"}</td>
                        <td>IQR je mezikvartilové rozpětí.</td>
                    </tr>

                </tbody>
            </table>

            <p>
            Podívejte se na aplikaci <ELink href="https://stdt.cz/histogram-app">Histogram</ELink>,
            na těchto stránkách, kde lze interaktivně experimentovat s histogramem.
            </p>

            <h2 id="hist-construction">Konstrukce sloupcového grafu / histogramu</h2>

            <p>Máme soubor naměřených hodnot. Zjistíme jeho rozsah {"$n$"} a variační obor
            {"$\\langle \\min_i x_i, \\max_i x_i \\rangle$"}. Určíme:
            </p>
            <ul>
                <li><strong>Počet</strong> {"$m$"} tříd (jedním z pravidel nahoře, nebo dle kontextu).</li>
                <li><strong>Délku</strong> {"$h$"} tříd. Běžně mívají třídy stejnou délku. Nikde však není psáno,
                že to tak nutně musí být - pokud ale použijeme/potřebujeme třídy různých délek, pak je
                potřeba dát pozor na interpretaci sloupcového grafu (!) - viz dole.
                </li>
                <li><strong>Hranice</strong> (popř. <strong>středy</strong>) tříd - tj. 'usazení tříd' na osu.</li>
            </ul>
            <p>... a to tak, aby třídy pokrývaly zjištěný variační obor (mohou ho přesahovat na obě strany),
            a aby měly rozumě "hezké" středy a hranice.
            </p>

            <p>Pozn. ke značení: Protože index {"$i$"} standardně používáme na indexaci surových dat,
            a symbol {"$n$"} označuje počet surových dat (rozsah souboru), tak pro indexování tříd
            použijeme jiný index ({"$j$"}), a pro počet tříd jiný symbol ({"$m$"}).
            </p>

            <p>Pro každou třídu určíme, kolik naměřených hodnot do ní spadá. Každá naměřená hodnota
            spadá právě do jedné třídy. Získaným číslům se říká <strong>absolutní četnosti</strong>;
            absolutní četnost třídy {"$j$"} označíme {"$c_j$"}. Podíl
            {`$$
                f_j = \\frac{c_j}{n}
            $$`}
            se nazývá <strong>relativní četnost</strong> třídy {"$j$"}. Např. pokud máme {`$50$`} naměřených hodnot
            a absolutní čentost dané třídy je {`$c_j = 8$`}, pak její relativní četnost je {`$f_j = \\frac{8}{50} = 0.16$`}, což nám říká,
            že do dané třídy spadá {`$16\\%$`} naměřených hodnot.
            </p>

            {/* TODO - alignment */}
            <table className='data-table histogram-table'>
                <thead>
                    <tr>
                        <th>Třída {"$\\langle a_j; b_j)$"}</th>
                        <th>Střed třídy {"$x^*_j = \\frac{a_j + b_j}{2}$"}</th>
                        <th>Absolutní četnost <span>{"$c_j$"}</span></th>
                        <th>Relativní četnost <span>{"$f_j = \\frac{c_j}{n}$"}</span></th>
                        <th>'Hustota'<br/>(Relativní četnost na jednotku délky) <span>{"$\\frac{f_j}{b_j-a_j}$"}</span></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$\\langle a_1; b_1)$"}</td>
                        <td>{"$x^*_1 = \\frac{a_1 + b_1}{2}$"}</td>
                        <td>{"$c_1$"}</td>
                        <td>{"$f_j = \\frac{c_1}{n}$"}</td>
                        <td>{"$\\frac{f_1}{b_1 - a_1}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle a_2; b_2)$"}</td>
                        <td>{"$x^*_2 = \\frac{a_2 + b_2}{2}$"}</td>
                        <td>{"$c_2$"}</td>
                        <td>{"$f_j = \\frac{c_2}{n}$"}</td>
                        <td>{"$\\frac{f_2}{b_2 - a_2}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\cdots$"}</td>
                        <td>{"$\\cdots$"}</td>
                        <td>{"$\\cdots$"}</td>
                        <td>{"$\\cdots$"}</td>
                        <td>{"$\\cdots$"}</td>
                    </tr>
                    <tr>
                        <td>{"$\\langle a_m; b_m)$"}</td>
                        <td>{"$x^*_m = \\frac{a_m + b_m}{2}$"}</td>
                        <td>{"$c_m$"}</td>
                        <td>{"$f_j = \\frac{c_m}{n}$"}</td>
                        <td>{"$\\frac{f_m}{b_m - a_m}$"}</td>
                    </tr>
                    <tr>
                        <td colSpan={2}>Graf:</td>
                        <td>Sloupcový graf (absolutních četností)</td>
                        <td>Sloupcový graf relativních četností</td>
                        <td>Histogram</td>
                    </tr>
                </tbody>
            </table>

            <p>Pozn.: Třídy na sebe navazují, takže {"$b_1 = a_2, b_2 = a_3$"} atd.</p>

            <p>Pozn.: Pokud jsou třídy stejně dlouhé, pak výraz {"$\\frac{f_j}{b_j - a_j}$"}
            nahradíme jednoduše výrazem {"$\\frac{f_j}{h}$"}. (Dělíme vždy stejnou délkou {"$h$"} třídy.)
            </p>

            <p>Graf zkonstruujeme tak, že nad třídu nakreslíme sloupec (obdélník) o dané výšce.
            Podle toho, který ukazatel použijeme (abs. četnost, rel. četnost, 'hustotu'), získáme
            různé grafy - viz tabulka. Termín <strong>histogram</strong> je u nás rezervován pro graf,
            kde je vynesena 'hustota'. Tato konvence není univerzální, některé zdroje by všechny tři grafy
            nazvaly histogramem, přičemž ten s 'hustotou' by nazvaly "normovaným histogramem".
            </p>

            <p>
            Histogram má tu vlastnost, že jeho <strong>celková plocha</strong> (tj. součet obsahu všech sloupců-obdélníků) <strong>je rovna 1</strong>.
            Tím se horní obálka sloupců chová jako hustota rozdělení spojité náhodné veličiny (viz. později v kurzu),
            a lze s ní pracovat jako s hustotou pravděpodobnosti.
            </p>

            <h2 id="hist-comp-discrete-continuous">Diskrétní vs spojitý znak</h2>
            <table className='simple-table'>
                <thead>
                    <tr>
                        <th></th>
                        <th>Diskrétní znak</th>
                        <th>Spojitý znak</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>sloupcový graf abs. četností</td>
                        <td>✓</td>
                        <td>✓</td>
                    </tr>
                    <tr>
                        <td>sloupcový graf rel. četností</td>
                        <td>✓</td>
                        <td>✓</td>
                    </tr>
                    <tr>
                        <td>histogram</td>
                        <td>✕</td>
                        <td>✓</td>
                    </tr>
                    <tr>
                        <td>vzhled grafu</td>
                        <td>mezery mezi sloupci</td>
                        <td>sloupce přiléhají k sobě (žádné mezery mezi sloupci)</td>
                    </tr>
                    <tr>
                        <td></td>
                        <td>
                            <div style={{paddingTop: 5}}>
                                <img src={gSloup} style={{width: 40}}/>
                            </div>
                        </td>
                        <td>
                            <div style={{paddingTop: 5}}>
                                <img src={gHisto} style={{width: 40}}/>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p>
            Pro diskrétní znak (ať už jakýkoli kvalitativní, nebo diskrétní kvantitativní),
            histogram nemá smysl, není definován. (V pravděpodobnosti uvidíme, že diskrétní
            náhodná veličina nemá hustotu.)
            </p>

            <h2 id="hist-comparison-graphs">Srovnání grafů</h2>

            <p>Dejme tomu, že jsme naměřili 20 hodnot absorbance v rozmezí
            cca. {"$0.5$"} až {"$0.9$"} a soubor roztřídili do 5 tříd. Níže jsou
            všechny tři grafy. Tvarově se nijak neliší (protože třídy jsou stejně
            dlouhé), liší se jen škálováním na ose {"$y$"}.
            </p>

            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={hist3graphs} style={{width: "90%"}}/>
            </div>

            <p>Pokud bychom zvolili délky tříd různě dlouhé, tvar histogramu (hustota),
            se může lišit od tvaru sloupcového grafu abs. i rel. četností. Níže je situace
            ukázána na jiné množině 20 naměřených hodnot:
            </p>

            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={hist3graphsB} style={{width: "90%"}}/>
            </div>

            <h2 id="hist-example">Ukázka</h2>

            <p>Máme naměřená data, která vypíšeme už seřazená podle velkikosti:
            {`$$
            \\begin{array}{cccccccccc}
            0.69 & 0.73 & 0.81 & 0.85 & 0.93 & 0.97 & 0.98 & 1.01 & 1.02 & 1.02 \\\\
            1.02 & 1.02 & 1.03 & 1.04 & 1.05 & 1.05 & 1.07 & 1.10 & 1.12 & 1.13 \\\\
            1.15 & 1.17 & 1.21 & 1.22 & 1.26 & 1.32 & 1.39 & 1.41 & 1.55 & 1.79
            \\end{array}
            $$`}
            Rozsah souboru: {"$n=30$"}, variační obor: {"$\\langle 0.69, 1.79 \\rangle$"}.
            </p>
            <p>
            Počet tříd? Sturges: {"$1 + \\log_2(30) \\approx 6$"}.
            </p>
            <p>
            Délka třídy? {"$h = \\frac{1.79 - 0.69}{6} = 0.18$"}. To není 'hezké' číslo,
            ale vidíme, že blízko je {"$0.2$"}. Když hranice budou {"$0.6, 0.8, 1.0, 1.2, 1.4, 1.6, 1.8$"},
            pak třídy budou mít středy {"$0.7, 0.9, 1.1, 1.3, 1.5, 1.7$"}. Jak bude vypadat histogram?
            </p>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={h06to18} style={{width: "70%"}}/>
            </div>

            <p>Kdybychom chtěli 'hezčí' středy tříd {"$\\ldots, 1.0, 1.2, \\ldots$"}, pak bychom potřebovali vzít
            sedm tříd, s hranicemi {"$0.5, 0.7, 0.9, 1.1, 1.3, 1.5, 1.7, 1.9$"}.
            </p>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={h05to19} style={{width: "70%"}}/>
            </div>
            <p>Vidíme, že tvar histogramu může být více či méně ovlivněn polohou tříd.</p>

            <p>Vezměme nyní interval {"$\\langle 0.60, 1.80 \\rangle$"}, rozdělme ho na třídy
            postupně s krokem:
            </p>
            <ul>
                <li>{"$h=0.1$"} (12 tříd),</li>
                <li>{"$h=0.05$"} (24 tříd),</li>
                <li>{"$h=0.01$"} (120 tříd),</li>
            </ul>
            <p>a podívejme se na histogramy:</p>

            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={h0p1} style={{width: "70%"}}/>
            </div>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={h0p05} style={{width: "70%"}}/>
            </div>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={h0p01} style={{width: "70%"}}/>
            </div>
            <p>
            Vidíme, že když je počet tříd příliš velký, názornost (shopnost histogramu
            zachytit tvar rozložení) se vytrácí. Pokud tříd na druhou stranu vezmeme příliš málo,
            histogram také není příliš informativní. Zde je délka třídy {"$h = 0.6$"} (dvě třídy):
            </p>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={h0p6} style={{width: "70%"}}/>
            </div>

            <p>
            Na závěr si ještě uvedeme tabulku použitou pro výpočet prvního histogramu:
            </p>

            <table className='data-table histogram-table histogram-table-ex'>
                <thead>
                    <tr>
                        <th>Index třídy {"$j$"}</th>
                        <th>Třída {"$\\langle a_j; b_j)$"}</th>
                        <th>Střed třídy {"$x^*_j = \\frac{a_j + b_j}{2}$"}</th>
                        <th>Absolutní četnost <span>{"$c_j$"}</span></th>
                        <th>Relativní četnost <span>{"$f_j = \\frac{c_j}{n}$"}</span></th>
                        <th>'Hustota'<br/>(Relativní četnost na jednotku délky) <span>{"$\\frac{f_j}{b_j-a_j}$"}</span></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$1$"}</td>
                        <td>{"$\\langle 0.6; 0.8)$"}</td>
                        <td>{"$0.7$"}</td>
                        <td>{"$2$"}</td>
                        <td>{"$\\frac{2}{30} = 0.0\\bar{6}$"}</td>
                        <td>{"$\\frac{0.0\\bar{6}}{0.2} = 0.3\\bar{3}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$2$"}</td>
                        <td>{"$\\langle 0.8; 1.0)$"}</td>
                        <td>{"$0.9$"}</td>
                        <td>{"$5$"}</td>
                        <td>{"$\\frac{5}{30} = 0.1\\bar{6}$"}</td>
                        <td>{"$\\frac{0.1\\bar{6}}{0.2} = 0.8\\bar{3}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$3$"}</td>
                        <td>{"$\\langle 1.0; 1.2)$"}</td>
                        <td>{"$1.1$"}</td>
                        <td>{"$15$"}</td>
                        <td>{"$\\frac{15}{30} = 0.50$"}</td>
                        <td>{"$\\frac{0.50}{0.2} = 2.50$"}</td>
                    </tr>
                    <tr>
                        <td>{"$4$"}</td>
                        <td>{"$\\langle 1.2; 1.4)$"}</td>
                        <td>{"$1.3$"}</td>
                        <td>{"$5$"}</td>
                        <td>{"$\\frac{5}{30} = 0.1\\bar{6}$"}</td>
                        <td>{"$\\frac{0.1\\bar{6}}{0.2} = 0.8\\bar{3}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$5$"}</td>
                        <td>{"$\\langle 1.4; 1.6)$"}</td>
                        <td>{"$1.5$"}</td>
                        <td>{"$2$"}</td>
                        <td>{"$\\frac{2}{30} = 0.0\\bar{6}$"}</td>
                        <td>{"$\\frac{0.0\\bar{6}}{0.2} = 0.3\\bar{3}$"}</td>
                    </tr>
                    <tr>
                        <td>{"$6$"}</td>
                        <td>{"$\\langle 1.6; 1.8)$"}</td>
                        <td>{"$1.7$"}</td>
                        <td>{"$1$"}</td>
                        <td>{"$\\frac{1}{30} = 0.0\\bar{3}$"}</td>
                        <td>{"$\\frac{0.0\\bar{3}}{0.2} = 0.1\\bar{6}$"}</td>
                    </tr>
                </tbody>
            </table>

            <Footer/>
        </ChapterWrapper>
    );
}
